import { createContext, useEffect, useMemo } from "react";
import axios from "../../utils/axios";
// settings
import actionTypes from "../../common/actionTypes";
import { initialState as shopInitialState } from "../../common/V2/shopConstants";
// context
import useAuthContext from "../../hooks/useAuthContext";
// reducers
import { shopReducer } from "../../reducers/V2/shopReducer";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";
// schemas

const SHOP_DB_INFO = "/v2/shop";
const SHOP_DOMAIN = "/v2/shop/domain";
const SHOP_DOMAIN_VALIDATE = "/v2/shop/domain/validate";
const SHOP_THEME = "/v2/shop/theme";

const initialState = {
  shop: { ...shopInitialState },
};

const ShopContext = createContext({
  ...initialState,
});

const ShopProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(shopReducer, initialState);
  const { firebaseAuthIdToken } = useAuthContext();

  useEffect(() => {
    if (!firebaseAuthIdToken) return;

    dispatch({ type: actionTypes.FETCH_SHOP });

    axios
      .get(SHOP_DB_INFO, {
        headers: {
          Authorization: firebaseAuthIdToken,
        },
      })
      .then(({ data }) => {
        const shop = data.data.existingShop;
        dispatch({
          type: actionTypes.FETCH_SHOP_SUCCESS,
          payload: {
            shop,
          },
        });
      })
      .catch((error) => {
        dispatch({
          type: actionTypes.FETCH_SHOP_FAILURE,
          error: error,
        });
      });
  }, [firebaseAuthIdToken]);

  const createDNSRecordInDB = async (email) => {
    try {
      const res = await axios.post(`${SHOP_DOMAIN}`, {
        email,
      });

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "Failed to create DNS records");
      }

      // Update the shop context with the new custom domain data
      dispatch({
        type: actionTypes.UPDATE_CUSTOM_DOMAIN,
        payload: res.data.data,
      });

      // Return the DNS records from the server response
      return res.data.data.dnsRecords || [];
    } catch (error) {
      // Handle specific server errors
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  };

  const validateDNSRecords = async () => {
    try {
      const res = await axios.post(`${SHOP_DOMAIN_VALIDATE}`);

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "DNS validation failed");
      }

      // Update the shop context with the updated custom domain data if available
      if (res.data.data.updatedDnsRecords || res.data.data.isValid !== undefined) {
        dispatch({
          type: actionTypes.UPDATE_CUSTOM_DOMAIN,
          payload: res.data.data,
        });
      }

      return {
        success: res.data.success,
        isValid: res.data.data.isValid,
        status: res.data.data.status,
        validationDetails: res.data.data.validationDetails,
        updatedDnsRecords: res.data.data.updatedDnsRecords,
      };
    } catch (error) {
      // Handle specific server errors
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }

      throw error;
    }
  };

  const getDNSRecords = async () => {
    try {
      dispatch({
        type: actionTypes.FETCH_CUSTOM_DOMAIN,
      });
      const res = await axios.get(`${SHOP_DOMAIN}`);
      dispatch({
        type: actionTypes.FETCH_CUSTOM_DOMAIN_SUCCESS,
        payload: res.data.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_CUSTOM_DOMAIN_FAILURE,
        error: error,
      });
      throw error;
    }
  };

  const updateThemeExtensionStatus = async (isEnabled) => {
    try {
      const res = await axios.post(`${SHOP_THEME}`, { isEnabled });

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "Failed to update theme extension status");
      }

      // Update the shop context with the new isEnabled status
      dispatch({
        type: actionTypes.UPDATE_THEME_EXTENSION_STATUS,
        payload: { isEnabled },
      });

      return res.data;
    } catch (error) {
      console.error("Error updating theme extension status:", error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      createDNSRecordInDB,
      getDNSRecords,
      validateDNSRecords,
      updateThemeExtensionStatus,
    }),
    [state]
  );

  return <ShopContext.Provider value={memoizedState}>{children}</ShopContext.Provider>;
};

export { ShopContext, ShopProvider };
